<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="320px"
    :scroll-strategy="'none'"
    :close-on-content-click="false"
  >
    <v-card>
      <v-card-item class="pa-0">
        <span class='edit-entity-title'>Edit Entity</span>
        <v-btn
          icon="mdi-close"
          variant="plain"
          class="ml-auto"
          @click="toggleDialog(false)"
        />
      </v-card-item>
      <v-card-text v-if="entity" class="entity-card" :style="{ 'border-color': cardTypeColor(entityCardType) }" ref="sortableList">
        <v-combobox
          density="compact"
          :model-value="entity"
          :style="{ border: '1px solid ' + cardTypeColor(entityCardType), marginBottom: '8px' }"
          disabled
        >
          <template #selection>
            <v-chip density="compact" class="mr-2">
              {{ entity.name }}
            </v-chip>
          </template>
        </v-combobox>
        <div v-for="(field, index) in entity.dataFields" :key="field.name" class="sortableRow">
          <entity-field-row
            :field="field"
            :entity="entity"
            :index="index"
            class="field-name d-flex align-center position-relative"
          />
        </div>
        <v-btn
          icon="mdi-plus"
          size="small"
          variant="plain"
          class="ml-auto"
          @click="addColumn(entityIndex)"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import EntityFieldRow from '@/components/EntityFieldRow.vue'
import useEntityActions from '@/composables/useEntityActions'
import useDomainModel from '@/composables/useDomainModel'
import Sortable from 'sortablejs'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'

const { modelValue, entity, refreshKey } = defineProps({ modelValue: Boolean, entity: Object, refreshKey: Number })
const emit = defineEmits(['update:modelValue'])

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)

const entityIndex = computed(() => (workflow.value.dataModels ?? []).findIndex((item) => item.id === entity?.id))
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const entityCardType = computed(() => diagramHelpers.methods.getAggregateCardType(requirementTypes.value))

const { addColumn } = useEntityActions()
const { cardTypeColor } = useDomainModel()

const sortableList = ref(null)
let sortable = null

const onEndDrag = async (dropEvent) => {
  const { oldDraggableIndex: fromIndex, newDraggableIndex: toIndex } = dropEvent
  const newDataModels = safeStructuredClone(workflow.value.dataModels || [])

  const field = newDataModels[entityIndex.value].dataFields[fromIndex]
  newDataModels[entityIndex.value].dataFields.splice(fromIndex, 1)
  newDataModels[entityIndex.value].dataFields.splice(toIndex, 0, field)

  await workflowApi.updateDataModels({
    dataModelsOnWorkflow: newDataModels,
    workflow: workflow.value
  })
}

const initSortable = () => {
  if (!sortableList.value) return

  sortable = new Sortable(sortableList.value.$el, {
    draggable: '.sortableRow',
    handle: '.sortHandle',
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    animation: 150,
    scroll: true,
    forceFallback: false,
    scrollSensitivity: 100,
    onEnd: onEndDrag,
    onStart: function (event) {
      Array.prototype.forEach.call(
        document.querySelectorAll('.v-row-group__header'),
        function (el, index) {
          if (!el.classList.contains('sortableRow') && index !== 0) {
            el.classList.add('sortableRow')
          }
        }
      )
      Array.prototype.forEach.call(
        document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
        function (el, index) {
          if (!el.classList.contains('white-background-on-hover') && index !== 0) {
            el.classList.add('white-background-on-hover')
          }
        }
      )
    }
  })
}

const destroySortable = () => {
  if (sortable) {
    sortable.destroy()
    sortable = null
  }
}

const toggleDialog = (value) => {
  emit('update:modelValue', value)
}

watch([() => modelValue, () => refreshKey], async ([dialogOpen]) => {
  if (dialogOpen) {
    await nextTick()
    destroySortable()
    initSortable()
  } else {
    destroySortable()
  }
}, { immediate: true })
</script>

<style scoped>
.v-card {
  border: 1px solid var(--dark-2);
}

:deep(.v-card-item__content) {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 10px;

  .edit-entity-title {
    font-size: 20px;
    color: var(--text-light) !important;
  }
}

.entity-card {
  min-width: 190px;
  max-width: 250px;
  margin: 12px auto 24px;
  padding: 12px !important;
  border-radius: 8px;
  background: #fff;
  border: 1px solid black;
  border-top-width: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
}
</style>
